import React, {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  GetAppraisalByGuid,
  GetEmployeeAppraisals,
  SaveSkipLevelReview,
  SubmitSkipLevelReview,
} from "./PMSService";
import "./pms.css";
import { TostMsg } from "../../../App";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader";

const PMSSkipLevelReview = () => {
  const { guid } = useParams();
  const history = useHistory();

  const [appraisal, setAppraisal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [showSendBackModal, setShowSendBackModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const [promoRecommended, setPromoRecommended] = useState(false);
  const [overallComment, setOverallComment] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const toster = useContext(TostMsg);

  /*
   * Stores skip-level KRA overrides.

   * Example:
   * {
   *   101: {
   *      rating: 4,
   *      comment: "..."
   *   }
   * }
   */
  const [kraOverrides, setKraOverrides] = useState({});

  /*
   * Stores question overrides.
   *
   * Example:
   * {
   *   161: {
   *      score: 4,
   *      comment: "..."
   *   }
   * }
   */
  const [questionOverrides, setQuestionOverrides] = useState({});

  // ---------------------------------------------------------
  // GET APPRAISAL
  // ---------------------------------------------------------

  useEffect(() => {
    if (!guid) return;

    getAppraisal();
  }, [guid]);

  // ---------------------------------------------------------
  // VALIDATE BEFORE OPENING SUBMIT MODAL
  // ---------------------------------------------------------

  const validateSubmitReview = () => {
    const errors = {};

    // --------------------------------------------
    // Validate KRAs
    // --------------------------------------------

    (appraisal?.Kras || []).forEach((kra, index) => {
      const kraId = getKraId(kra);

      if (!kraId) return;

      const rating = kraOverrides[kraId]?.rating ?? getKraManagerRating(kra);

      const comment = kraOverrides[kraId]?.comment || "";

      // Rating validation
      if (
        rating === null ||
        rating === undefined ||
        rating === "" ||
        Number(rating) < 1 ||
        Number(rating) > 5
      ) {
        errors[`kra_${kraId}_rating`] =
          `Please select a rating for KRA ${index + 1}.`;
      }

      // Comment validation
      if (!comment.trim()) {
        errors[`kra_${kraId}_comment`] =
          `Please enter a comment for KRA ${index + 1}.`;
      }
    });

    // --------------------------------------------
    // Validate Questions
    // --------------------------------------------

    (appraisal?.Questions || []).forEach((question, index) => {
      const questionId = question?.QuestionId;

      if (!questionId) return;

      const score =
        questionOverrides[questionId]?.score ??
        getQuestionManagerScore(question);

      const comment = questionOverrides[questionId]?.comment || "";

      // Score validation
      if (
        score === null ||
        score === undefined ||
        score === "" ||
        Number(score) < 1 ||
        Number(score) > 5
      ) {
        errors[`question_${questionId}_score`] =
          `Please select a score for Question ${index + 1}.`;
      }

      // Reason validation
      if (!comment.trim()) {
        errors[`question_${questionId}_comment`] =
          `Please enter the reason for Question ${index + 1}.`;
      }
    });

    // --------------------------------------------
    // Overall comment
    // --------------------------------------------

    // if (!overallComment.trim()) {
    //   errors.overallComment =
    //     "Please enter the overall skip-level comment.";
    // }

    setValidationErrors(errors);

    // --------------------------------------------
    // If errors exist, don't open popup
    // --------------------------------------------

    if (Object.keys(errors).length > 0) {
      return false;
    }

    // Everything valid
    return true;
  };

  // ---------------------------------------------------------
  // SAVE SKIP LEVEL DRAFT
  // ---------------------------------------------------------

  const handleSaveDraft = async () => {
    if (!appraisal?.AppraisalId) {
      console.error("Appraisal ID is missing");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        KraOverrides: (appraisal?.Kras || [])
          .map((kra) => {
            const kraId = getKraId(kra);

            if (!kraId) return null;

            const managerRating = getKraManagerRating(kra);

            const override = kraOverrides[kraId];

            const rating = Number(override?.rating ?? managerRating ?? 0);

            const comment = override?.comment || "";

            return {
              KraSnapshotId: Number(kraId),
              Rating: rating,
              Comment: comment,
            };
          })
          .filter(Boolean),

        CriteriaOverrides: (appraisal?.Questions || [])
          .map((question) => {
            const questionId = question?.QuestionId;

            if (!questionId) return null;

            const managerScore = getQuestionManagerScore(question);

            const override = questionOverrides[questionId];

            const score = Number(override?.score ?? managerScore ?? 0);

            const comment = override?.comment || "";

            return {
              QuestionId: Number(questionId),
              Score: score,
              Comment: comment,
            };
          })
          .filter(Boolean),

        IsPromotionRecommended: Boolean(promoRecommended),

        OverallComment: overallComment || "",
      };

      console.log("Skip Level Save Draft Payload:", payload);

      const response = await SaveSkipLevelReview(
        Number(appraisal.AppraisalId),
        payload,
      );

      console.log("Skip level draft saved:", response);

      // Optional toast if you already have toast context
      // toster?.current?.success(
      //   response?.Message ||
      //   response?.message ||
      //   "Draft saved successfully."
      // );
      if (response?.StatusCode === 2000) {
        toster.current.success(
          response?.Message ||
            response?.message ||
            "Skip-Level draft saved successfully.",
        );

        // Refresh data from API
        await getAppraisal();
      }

      setLoading(false);
    } catch (error) {
      console.error("Skip level draft save failed:", error);

      toster.current.error(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "Failed to save Skip-Level draft.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getAppraisal = async () => {
    try {
      setLoading(true);

      const params = {
        Guid: guid,
      };

      const response = await GetEmployeeAppraisals(params);

      const data = response?.Data ?? response;

      if (!data) {
        console.error("No appraisal data found");
        return;
      }

      setAppraisal(data);

      setPromoRecommended(Boolean(data?.IsPromotionRecommended));

      setOverallComment(data?.SkipLevelOverallComment || "");

      // --------------------------------------------
      // Initialize KRA overrides
      // --------------------------------------------

      const initialKraOverrides = {};

      (data?.Kras || []).forEach((kra) => {
        const kraId =
          kra?.KraSnapshotId ?? kra?.KRASnapshotId ?? kra?.Id ?? kra?.KraId;

        if (!kraId) return;

        const managerRating =
          kra?.Rating ?? kra?.ManagerRating ?? kra?.EffectiveScore ?? 0;

        initialKraOverrides[kraId] = {
          rating: Number(managerRating || 0),
          comment: kra?.SkipLevelComment || "",
        };
      });

      setKraOverrides(initialKraOverrides);

      // --------------------------------------------
      // Initialize question overrides
      // --------------------------------------------

      const initialQuestionOverrides = {};

      (data?.Questions || []).forEach((question) => {
        const questionId = question?.QuestionId;

        if (!questionId) return;

        const managerScore =
          question?.EffectiveScore ??
          question?.ManagerScore ??
          question?.Score ??
          0;

        initialQuestionOverrides[questionId] = {
          score: Number(managerScore || 0),
          comment: question?.SkipLevelComment || "",
        };
      });

      setQuestionOverrides(initialQuestionOverrides);
    } catch (error) {
      console.error("Get appraisal failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // HELPERS
  // ---------------------------------------------------------

  const getKraId = (kra) =>
    kra?.KraSnapshotId ?? kra?.KRASnapshotId ?? kra?.Id ?? kra?.KraId;

  const getKraTitle = (kra) =>
    kra?.KraTitle ?? kra?.Title ?? kra?.Name ?? kra?.KRAName ?? "KRA";

  const getKraManagerRating = (kra) =>
    Number(kra?.ManagerRating ?? kra?.EffectiveRating ?? 0);

  const getQuestionManagerScore = (question) =>
    Number(
      question?.EffectiveScore ??
        question?.ManagerScore ??
        question?.Score ??
        0,
    );

  // ---------------------------------------------------------
  // KRA RATING CHANGE
  // ---------------------------------------------------------

  const handleKraRatingChange = (kraId, rating) => {
    setKraOverrides((prev) => ({
      ...prev,
      [kraId]: {
        ...(prev[kraId] || {}),
        rating,
      },
    }));
  };

  const handleKraCommentChange = (kraId, comment) => {
    setKraOverrides((prev) => ({
      ...prev,
      [kraId]: {
        ...(prev[kraId] || {}),
        comment,
      },
    }));
  };

  // ---------------------------------------------------------
  // QUESTION SCORE CHANGE
  // ---------------------------------------------------------

  const handleQuestionScoreChange = (questionId, score) => {
    setQuestionOverrides((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        score,
      },
    }));
  };

  const handleQuestionCommentChange = (questionId, comment) => {
    setQuestionOverrides((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        comment,
      },
    }));
  };

  // ---------------------------------------------------------
  // CALCULATE KRA SCORE
  // ---------------------------------------------------------

  const kraScore = useMemo(() => {
    if (!appraisal?.Kras?.length) return 0;

    let totalWeightedScore = 0;
    let totalWeight = 0;

    appraisal.Kras.forEach((kra) => {
      const kraId = getKraId(kra);

      const rating = kraOverrides[kraId]?.rating ?? getKraManagerRating(kra);

      const weight = Number(
        kra?.Weightage ?? kra?.Weight ?? kra?.KraWeightage ?? 0,
      );

      if (weight > 0) {
        totalWeightedScore += Number(rating || 0) * weight;
        totalWeight += weight;
      }
    });

    if (!totalWeight) {
      const ratings = appraisal.Kras.map((kra) => {
        const kraId = getKraId(kra);

        return Number(
          kraOverrides[kraId]?.rating ?? getKraManagerRating(kra) ?? 0,
        );
      });

      if (!ratings.length) return 0;

      return ratings.reduce((sum, value) => sum + value, 0) / ratings.length;
    }

    return totalWeightedScore / totalWeight;
  }, [appraisal, kraOverrides]);

  // ---------------------------------------------------------
  // CALCULATE QUESTION SCORE
  // ---------------------------------------------------------

  const criteriaScore = useMemo(() => {
    if (!appraisal?.Questions?.length) return 0;

    let totalWeightedScore = 0;
    let totalWeight = 0;

    appraisal.Questions.forEach((question) => {
      const questionId = question?.QuestionId;

      const score =
        questionOverrides[questionId]?.score ??
        getQuestionManagerScore(question);

      const weight = Number(question?.Weightage || 0);

      if (weight > 0) {
        totalWeightedScore += Number(score || 0) * weight;
        totalWeight += weight;
      }
    });

    if (!totalWeight) {
      const scores = appraisal.Questions.map((question) => {
        const questionId = question?.QuestionId;

        return Number(
          questionOverrides[questionId]?.score ??
            getQuestionManagerScore(question) ??
            0,
        );
      });

      if (!scores.length) return 0;

      return scores.reduce((sum, value) => sum + value, 0) / scores.length;
    }

    return totalWeightedScore / totalWeight;
  }, [appraisal, questionOverrides]);

  // ---------------------------------------------------------
  // FINAL WEIGHTED SCORE
  // ---------------------------------------------------------

  const adjustedScore = useMemo(() => {
    if (!appraisal) return 0;

    const kraWeight = Number(appraisal?.KraSectionWeightage || 0) / 100;

    const criteriaWeight =
      Number(appraisal?.CriteriaSectionWeightage || 0) / 100;

    return (
      Number(kraScore || 0) * kraWeight +
      Number(criteriaScore || 0) * criteriaWeight
    );
  }, [appraisal, kraScore, criteriaScore]);

  // ---------------------------------------------------------
  // SUBMIT SKIP LEVEL
  // ---------------------------------------------------------

  const handleOpenSubmitModal = () => {
    const isValid = validateSubmitReview();

    if (!isValid) {
      return;
    }

    setShowSubmitModal(true);
  };

  const handleSubmitSkipLevel = async () => {
    if (!appraisal?.AppraisalId) return;

    try {
      setLoading(true);

      const payload = {
        KraOverrides: (appraisal?.Kras || [])
          .map((kra) => {
            const kraId = getKraId(kra);

            if (!kraId) return null;

            const managerRating = getKraManagerRating(kra);

            const override = kraOverrides[kraId];

            const rating = Number(override?.rating ?? managerRating);

            const comment = override?.comment || "";

            return {
              KraSnapshotId: Number(kraId),
              Rating: rating,
              Comment: comment,
            };
          })
          .filter(Boolean),

        CriteriaOverrides: (appraisal?.Questions || [])
          .map((question) => {
            const questionId = question?.QuestionId;

            if (!questionId) return null;

            const managerScore = getQuestionManagerScore(question);

            const override = questionOverrides[questionId];

            const score = Number(override?.score ?? managerScore);

            const comment = override?.comment || "";

            return {
              QuestionId: Number(questionId),
              Score: score,
              Comment: comment,
            };
          })
          .filter(Boolean),

        IsPromotionRecommended: promoRecommended,
        OverallComment: "",
      };

      console.log("Skip Level Submit Payload:", payload);

      const response = await SubmitSkipLevelReview(
        appraisal.AppraisalId,
        payload,
      );

      console.log("Skip level submitted:", response);

      setShowSubmitModal(false);

      if (response?.StatusCode === 2000) {
        await getAppraisal();
        toster.current.success("Skip-level review submit successfully");
      }
      setLoading(false);
      // refresh latest appraisal

      // optionally go back to list
      // history.goBack();
    } catch (error) {
      console.error("Skip level submit failed:", error);
      toster.current.success(
        "Failed to skip-level review submitting. Please try again",
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // LOADING
  // ---------------------------------------------------------

  if (loading) {
    return (
      <div className="container-fluid">
        <div
          style={{
            padding: 40,
            textAlign: "center",
            color: "#8492a6",
          }}
        >
          Loading appraisal...
        </div>
      </div>
    );
  }

  const employeeName = appraisal?.EmployeeName || "Employee";

  const managerName = appraisal?.FirstReviewerName || "-";

  const employeeInitials = employeeName
    .split(" ")
    .map((x) => x?.[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="container-fluid">
      <div className="row g-3 assessment-module">
        <div className="og-main">
          {/* =====================================================
              HEADER
          ===================================================== */}

          <div className="og-page-header">
            <div>
              <div className="og-breadcrumb">
                <a
                  href="#"
                  style={{
                    color: "#EC4119",
                    textDecoration: "none",
                  }}
                >
                  Performance
                </a>{" "}
                ›{" "}
                <a
                  href="#"
                  style={{
                    color: "#EC4119",
                    textDecoration: "none",
                  }}
                >
                  Skip-Level Reviews
                </a>{" "}
                › {employeeName}
              </div>

              <div className="og-page-title">Skip-Level Review</div>

              <div className="og-page-sub">
                Reviewing: <strong>{employeeName}</strong>
                {" · "}
                {appraisal?.Designation || "-"}
                {" · "}
                Manager: {managerName}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
              }}
            >
              {appraisal?.CurrentStageId < 3 && (
                <button
                  className="btn-og btn-og-danger btn-og-sm"
                  type="button"
                  onClick={() => setShowSendBackModal(true)}
                >
                  <i className="fa fa-rotate-left"></i> Send Back
                </button>
              )}

              {appraisal?.CurrentStageId <= 3 &&
                appraisal?.CurrentStageId != 1 && (
                  <>
                    <button
                      className="btn-og btn-og-secondary"
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={submitting}
                    >
                      <i className="fa fa-save"></i> Save Draft
                    </button>

                    <button
                      className="btn-og btn-og-primary"
                      type="button"
                      onClick={handleOpenSubmitModal}
                    >
                      <i className="fa fa-check"></i> Submit Review
                    </button>
                  </>
                )}
            </div>
          </div>

          {/* =====================================================
              ROLE INFO
          ===================================================== */}

          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #fbd38d",
              borderRadius: 10,
              padding: "12px 16px",
              marginBottom: 16,
              fontSize: 13,
              color: "#744210",
            }}
          >
            <i className="fa fa-circle-info" style={{ color: "#d97706" }}></i>
            <strong> Skip-Level Role:</strong> Review the manager's assessment
            for fairness and consistency. You may override any KRA rating,
            question score, or overall comment. Your changes are tracked
            separately.
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 260px",
              gap: 16,
            }}
          >
            <div>
              {/* =====================================================
                  EMPLOYEE CARD
              ===================================================== */}

              <div
                className="og-card"
                style={{
                  marginBottom: 14,
                  padding: "14px 16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 20,
                    alignItems: "center",
                  }}
                >
                  <div className="og-emp-cell">
                    <div
                      className="og-emp-av"
                      style={{
                        background: "#EC4119",
                        width: 40,
                        height: 40,
                        fontSize: 14,
                      }}
                    >
                      {employeeInitials}
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                        }}
                      >
                        {employeeName}
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          color: "#8492a6",
                        }}
                      >
                        {appraisal?.Designation || "-"} ·{" "}
                        {appraisal?.Department || "-"} · Employee ID:{" "}
                        {appraisal?.EmpId || "-"}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      marginLeft: "auto",
                      display: "flex",
                      gap: 16,
                      fontSize: 13,
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: 10.5,
                          color: "#8492a6",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Manager Score
                      </div>

                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                        }}
                      >
                        {Number(appraisal?.ComputedScore || 0).toFixed(2)}
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: 10.5,
                          color: "#8492a6",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Adjusted Score
                      </div>

                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#EC4119",
                        }}
                      >
                        {Number(adjustedScore || 0).toFixed(2)}
                      </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: 10.5,
                          color: "#8492a6",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        Manager Promo
                      </div>

                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#8492a6",
                          marginTop: 4,
                        }}
                      >
                        {appraisal?.IsPromotionRecommended
                          ? "Recommended"
                          : "Not Rec."}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* =====================================================
                  KRA SECTION
              ===================================================== */}

              <div className="og-card" style={{ marginBottom: 14 }}>
                <div className="og-section-hd">
                  <div className="og-section-num">A</div>

                  <div className="og-section-title">KRA Assessment</div>

                  <div className="og-section-wt">
                    {appraisal?.KraSectionWeightage || 0}% of final score
                  </div>
                </div>

                {(appraisal?.Kras || []).length === 0 ? (
                  <div
                    style={{
                      padding: 20,
                      textAlign: "center",
                      color: "#8492a6",
                    }}
                  >
                    No KRA data available.
                  </div>
                ) : (
                  appraisal.Kras.map((kra, kraIndex) => {
                    const kraId = getKraId(kra);

                    const managerRating = getKraManagerRating(kra);

                    const selectedRating =
                      kraOverrides[kraId]?.rating ?? managerRating;

                    const comment = kraOverrides[kraId]?.comment || "";

                    return (
                      <div
                        className="og-kra-card"
                        key={kraId || kraIndex}
                        style={{
                          marginBottom:
                            kraIndex !== appraisal.Kras.length - 1 ? 12 : 0,
                        }}
                      >
                        <div className="og-kra-header">
                          <div>
                            <div className="og-kra-title">
                              {getKraTitle(kra)}
                            </div>
                          </div>
                        </div>
                        <div className="ar-kpi-box">
                          <div className="ar-kpi-box-title">
                            Employee KPI Responses
                          </div>
                        </div>
                        {/* KRA KPIs */}
                        {(kra?.Kpis || kra?.KPIs || kra?.KraKpis || []).map(
                          (kpi, kpiIndex) => (
                            <div
                              key={kpi?.KpiId || kpi?.KPISnapshotId || kpiIndex}
                              className="ar-kpi-box"
                            >
                              <div className="og-kpi-item">
                                <div className="og-kpi-dot ar-kpi-dot"></div>
                                <div className="ar-flex1">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <span className="ar-kpi-title">
                                      {kpi?.KpiDescription || "KPI"}
                                    </span>

                                    {kpi?.CompletionPct != null && (
                                      <>
                                        <div>
                                          <b>Completion(%):</b>{" "}
                                          <span className="ar-kpi-pct">
                                            {kpi.CompletionPct}%
                                          </span>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      height: 5,
                                      background: "#e2e8f0",
                                      borderRadius: 6,
                                      overflow: "hidden",
                                      margin: "3px 0",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: `${Math.min(
                                          100,
                                          Math.max(0, kpi?.CompletionPct),
                                        )}%`,
                                        height: "100%",
                                        background: "#38a169",
                                        borderRadius: 6,
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>

                              {kpi?.EmployeeNarrative && (
                                <div className="ar-kpi-narr">
                                  {kpi?.EmployeeNarrative}
                                </div>
                              )}
                            </div>
                          ),
                        )}

                        {/* Manager Rating */}
                        <div
                          style={{
                            background: "#f0fff4",
                            border: "1px solid #c6f6d5",
                            borderRadius: 8,
                            padding: "10px 12px",
                            marginBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#276749",
                              marginBottom: 6,
                            }}
                          >
                            <i className="fa fa-user-check"></i> Manager Rating:{" "}
                            <strong
                              style={{
                                fontSize: 14,
                              }}
                            >
                              {managerRating} / 5
                            </strong>
                          </div>

                          {(kra?.ManagerComment || kra?.Comment) && (
                            <div
                              style={{
                                fontSize: 12,
                                color: "#276749",
                                fontStyle: "italic",
                              }}
                            >
                              {kra?.ManagerComment || kra?.Comment}
                            </div>
                          )}
                        </div>

                        {/* Override */}
                        {/* <div
                          style={{
                            background: "#fff",
                            border: "1.5px solid #7c3aed",
                            borderRadius: 10,
                            padding: "12px 14px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#7c3aed",
                              marginBottom: 8,
                            }}
                          >
                            <i className="fa fa-star"></i> Your Rating Override
                          </div>

                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              marginBottom: 5,
                            }}
                          >
                            {[1, 2, 3, 4, 5].map((value) => (
                              <button
                                key={value}
                                className={`btn-og ${
                                  selectedRating === value
                                    ? "btn-og-primary"
                                    : "btn-og-secondary"
                                } btn-og-sm`}
                                style={{
                                  minWidth: 40,
                                  background:
                                    selectedRating === value
                                      ? "#7c3aed"
                                      : undefined,
                                }}
                                onClick={() =>
                                  handleKraRatingChange(kraId, value)
                                }
                                type="button"
                              >
                                {value}
                              </button>
                            ))}
                          </div>

                          {validationErrors[`kra_${kraId}_rating`] && (
                            <div
                              style={{
                                color: "#e53e3e",
                                fontSize: 11,
                                marginBottom: 8,
                              }}
                            >
                              {validationErrors[`kra_${kraId}_rating`]}
                            </div>
                          )}

                          <div>
                            <label className="og-label">
                              Your Comment on this KRA
                            </label>

                            <textarea
                              className="og-textarea"
                              style={{
                                minHeight: 54,
                                border: validationErrors[`kra_${kraId}_comment`]
                                  ? "1px solid #e53e3e"
                                  : undefined,
                              }}
                              value={comment}
                              onChange={(e) =>
                                handleKraCommentChange(kraId, e.target.value)
                              }
                            />

                            {validationErrors[`kra_${kraId}_comment`] && (
                              <div
                                style={{
                                  color: "#e53e3e",
                                  fontSize: 11,
                                  marginTop: 5,
                                }}
                              >
                                {validationErrors[`kra_${kraId}_comment`]}
                              </div>
                            )}
                          </div>
                        </div> */}
                      </div>
                    );
                  })
                )}
              </div>

              {/* =====================================================
                  QUESTIONS
              ===================================================== */}

              <div className="og-card" style={{ marginBottom: 14 }}>
                <div className="og-section-hd">
                  <div className="og-section-num">B</div>

                  <div className="og-section-title">
                    Current Cycle Evaluation
                  </div>

                  <div className="og-section-wt">
                    {appraisal?.CriteriaSectionWeightage || 0}% of final score
                  </div>
                </div>

                {(appraisal?.Questions || []).length === 0 ? (
                  <div
                    style={{
                      padding: 20,
                      textAlign: "center",
                      color: "#8492a6",
                    }}
                  >
                    No questions available.
                  </div>
                ) : (
                  appraisal.Questions.map((question, index) => {
                    const questionId = question?.QuestionId;

                    const managerScore = getQuestionManagerScore(question);

                    const selectedScore =
                      questionOverrides[questionId]?.score ?? managerScore;

                    const comment =
                      questionOverrides[questionId]?.comment || "";

                    return (
                      <div
                        key={questionId || index}
                        style={{
                          padding: "12px 0",
                          borderBottom:
                            index !== appraisal.Questions.length - 1
                              ? "1px solid #f1f3f7"
                              : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              color: "#8492a6",
                              textTransform: "uppercase",
                              background: "#f1f3f7",
                              padding: "2px 8px",
                              borderRadius: 4,
                            }}
                          >
                            {question?.SectionName || "Criteria"}
                          </span>

                          <span
                            style={{
                              color: "#EC4119",
                              fontSize: 10.5,
                              fontWeight: 600,
                            }}
                          >
                            {question?.Weightage || 0}%
                          </span>
                        </div>

                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginBottom: 8,
                          }}
                        >
                          {question?.QuestionText}
                        </div>

                        {question?.EmployeeAnswer && (
                          <div
                            style={{
                              background: "#f8f9fb",
                              borderRadius: 8,
                              padding: "8px 12px",
                              marginBottom: 6,
                              fontSize: 12,
                              color: "#5f6b7c",
                            }}
                          >
                            <strong
                              style={{
                                color: "#8492a6",
                              }}
                            >
                              Employee:
                            </strong>{" "}
                            <p style={{ whiteSpace: "pre-wrap" }}>
                              {" "}
                              {question.EmployeeAnswer}
                            </p>
                          </div>
                        )}

                        <div
                          style={{
                            background: "#f0fff4",
                            border: "1px solid #c6f6d5",
                            borderRadius: 8,
                            padding: "8px 12px",
                            marginBottom: 10,
                            fontSize: 12,
                            color: "#276749",
                          }}
                        >
                          <strong>Manager score: {managerScore}/5</strong>

                          {question?.ManagerComment && (
                            <>
                              {" "}
                              <p style={{ whiteSpace: "pre-wrap" }}>
                                {question.ManagerComment}
                              </p>
                            </>
                          )}
                        </div>

                        {/* <div
                          style={{
                            display: "flex",
                            gap: 8,
                          }}
                        >
                          {[1, 2, 3, 4, 5].map((value) => (
                            <button
                              key={value}
                              className={`btn-og ${
                                selectedScore === value
                                  ? "btn-og-primary"
                                  : "btn-og-secondary"
                              } btn-og-sm`}
                              style={{
                                minWidth: 40,
                                background:
                                  selectedScore === value
                                    ? "#7c3aed"
                                    : undefined,
                              }}
                              onClick={() =>
                                handleQuestionScoreChange(questionId, value)
                              }
                              type="button"
                            >
                              {value}
                            </button>
                          ))}
                        </div> */}

                        {validationErrors[`question_${questionId}_score`] && (
                          <div
                            style={{
                              color: "#e53e3e",
                              fontSize: 11,
                              marginTop: 5,
                            }}
                          >
                            {validationErrors[`question_${questionId}_score`]}
                          </div>
                        )}

                        <div
                          style={{
                            marginTop: 8,
                          }}
                        >
                          <label className="og-label">
                            Reason for Override
                          </label>

                          <textarea
                            className="og-textarea"
                            style={{
                              minHeight: 50,
                            }}
                            value={comment}
                            onChange={(e) =>
                              handleQuestionCommentChange(
                                questionId,
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* =====================================================
                  COMPUTED SCORE
              ===================================================== */}

              <div className="og-computed-score" style={{ marginBottom: 14 }}>
                <div>
                  <div className="og-cs-label">
                    Weighted Score After Your Review
                  </div>

                  <div className="og-cs-value">
                    {Number(adjustedScore || 0).toFixed(2)}

                    <span
                      style={{
                        fontSize: 16,
                        fontWeight: 400,
                        opacity: 0.6,
                      }}
                    >
                      / 5.00
                    </span>
                  </div>

                  <div
                    style={{
                      fontSize: 11,
                      color: "rgba(255,255,255,0.5)",
                      marginTop: 2,
                    }}
                  >
                    KRA: {Number(kraScore || 0).toFixed(2)} ×{" "}
                    {appraisal?.KraSectionWeightage || 0}% + Criteria:{" "}
                    {Number(criteriaScore || 0).toFixed(2)} ×{" "}
                    {appraisal?.CriteriaSectionWeightage || 0}%
                  </div>
                </div>

                <div
                  style={{
                    marginLeft: "auto",
                    textAlign: "right",
                  }}
                >
                  <div className="og-cs-label">Final label set by</div>

                  <div
                    style={{
                      fontSize: 13,
                      color: "rgba(255,255,255,0.6)",
                    }}
                  >
                    HR at Normalization
                  </div>
                </div>
              </div>

              {/* =====================================================
                  PROMOTION
              ===================================================== */}

              <div
                className="og-card"
                style={{
                  marginBottom: 14,
                  border: "1.5px solid #fbd38d",
                }}
              >
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 700,
                    color: "#1a1a2e",
                    marginBottom: 10,
                  }}
                >
                  <i
                    className="fa fa-arrow-trend-up"
                    style={{
                      color: "#d97706",
                    }}
                  ></i>{" "}
                  Promotion Recommendation
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "#5f6b7c",
                        lineHeight: 1.6,
                      }}
                    >
                      Manager promotion recommendation:{" "}
                      <strong>
                        {appraisal?.IsPromotionRecommended
                          ? "Recommended"
                          : "Not Recommended"}
                      </strong>
                    </div>
                  </div>

                  <div
                    style={{
                      textAlign: "center",
                      flexShrink: 0,
                    }}
                  >
                    <label
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: 48,
                          height: 26,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={promoRecommended}
                          onChange={() => setPromoRecommended((prev) => !prev)}
                          style={{
                            opacity: 0,
                            width: 0,
                            height: 0,
                          }}
                        />

                        <span
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: promoRecommended
                              ? "#7c3aed"
                              : "#e9ecf0",
                            borderRadius: 13,
                            transition: ".2s",
                          }}
                        />

                        <span
                          style={{
                            position: "absolute",
                            left: promoRecommended ? 25 : 3,
                            top: 3,
                            width: 20,
                            height: 20,
                            background: "#fff",
                            borderRadius: "50%",
                            transition: ".2s",
                            boxShadow: "0 1px 4px rgba(0,0,0,.2)",
                          }}
                        />
                      </div>

                      <span
                        style={{
                          fontSize: 11.5,
                          fontWeight: 600,
                          color: "#8492a6",
                        }}
                      >
                        {promoRecommended ? "Recommended" : "Not Recommended"}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* =====================================================
                  OVERALL COMMENT
              ===================================================== */}
              {/* 
              <div className="og-card">
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    marginBottom: 10,
                  }}
                >
                  Overall Skip-Level Comment
                </div>

                <textarea
                  className="og-textarea"
                  style={{
                    minHeight: 90,
                  }}
                  value={overallComment}
                  onChange={(e) =>
                    setOverallComment(
                      e.target.value
                    )
                  }
                />
              </div> */}
            </div>

            {/* =====================================================
                RIGHT SIDE
            ===================================================== */}

            <div>
              <div className="og-card" style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#8492a6",
                    textTransform: "uppercase",
                    letterSpacing: ".4px",
                    marginBottom: 10,
                  }}
                >
                  Score Comparison
                </div>

                <div
                  style={{
                    fontSize: 13,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span
                      style={{
                        color: "#8492a6",
                      }}
                    >
                      Manager score
                    </span>

                    <strong>
                      {Number(appraisal?.ComputedScore || 0).toFixed(2)}
                    </strong>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span
                      style={{
                        color: "#7c3aed",
                        fontWeight: 600,
                      }}
                    >
                      Your adjusted score
                    </span>

                    <strong
                      style={{
                        color: "#7c3aed",
                      }}
                    >
                      {Number(adjustedScore || 0).toFixed(2)}
                    </strong>
                  </div>

                  <div
                    style={{
                      height: 1,
                      background: "#f1f3f7",
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 11,
                    }}
                  >
                    <span
                      style={{
                        color: "#8492a6",
                      }}
                    >
                      KRA overrides
                    </span>

                    <span
                      style={{
                        color: "#d97706",
                      }}
                    >
                      {Object.keys(kraOverrides).length}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 11,
                    }}
                  >
                    <span
                      style={{
                        color: "#8492a6",
                      }}
                    >
                      Question overrides
                    </span>

                    <span
                      style={{
                        color: "#d97706",
                      }}
                    >
                      {Object.keys(questionOverrides).length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Timeline */}

              <div className="og-card" style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#8492a6",
                    textTransform: "uppercase",
                    letterSpacing: ".4px",
                    marginBottom: 10,
                  }}
                >
                  Timeline
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    fontSize: 12.5,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                    }}
                  >
                    <i
                      className="fa fa-circle-check"
                      style={{
                        color: "#48bb78",
                      }}
                    ></i>

                    <span>
                      Self Assessment —{" "}
                      {appraisal?.SelfAssessmentDeadline || "-"}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                    }}
                  >
                    <i
                      className="fa fa-circle-check"
                      style={{
                        color: "#48bb78",
                      }}
                    ></i>

                    <span>
                      Manager — {appraisal?.ManagerReviewDeadline || "-"}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                    }}
                  >
                    <i
                      className="fa fa-clock"
                      style={{
                        color: "#EC4119",
                      }}
                    ></i>

                    <strong
                      style={{
                        color: "#EC4119",
                      }}
                    >
                      Skip-Level — {appraisal?.SkipLevelReviewDeadline || "-"}
                    </strong>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      opacity: 0.4,
                    }}
                  >
                    <i className="fa fa-circle" />

                    <span>
                      Normalization — {appraisal?.NormalizationDeadline || "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Audit */}

              <div className="og-card">
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#8492a6",
                    textTransform: "uppercase",
                    letterSpacing: ".4px",
                    marginBottom: 10,
                  }}
                >
                  Audit Trail
                </div>

                {(appraisal?.History || []).length === 0 ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#8492a6",
                    }}
                  >
                    No history available.
                  </div>
                ) : (
                  appraisal.History.map((historyItem, index) => (
                    <div
                      key={index}
                      style={{
                        fontSize: 12,
                        padding: 8,
                        background: "#f8f9fb",
                        borderRadius: 6,
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                        }}
                      >
                        {historyItem?.Action ||
                          historyItem?.StageName ||
                          "Activity"}
                      </div>

                      <div
                        style={{
                          color: "#8492a6",
                        }}
                      >
                        {historyItem?.CreatedOn || historyItem?.Date || ""}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* =====================================================
              SUBMIT MODAL
          ===================================================== */}

          {showSubmitModal && (
            <div
              className={`og-modal-overlay ${showSubmitModal ? "open" : ""}`}
            >
              <div className="og-modal">
                <div className="og-modal-header">
                  <div className="og-modal-title">
                    <i
                      className="fa fa-check-circle"
                      style={{
                        color: "#48bb78",
                      }}
                    ></i>{" "}
                    Submit Skip-Level Review
                  </div>

                  <button
                    className="og-modal-close"
                    onClick={() => setShowSubmitModal(false)}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>

                <div className="modal-body">
                  <div
                    style={{
                      textAlign: "center",
                      padding: "10px 0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 42,
                        marginBottom: 10,
                      }}
                    >
                      🔍
                    </div>

                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        marginBottom: 6,
                      }}
                    >
                      Submit Skip-Level Review for {employeeName}?
                    </div>

                    <div
                      style={{
                        fontSize: 13,
                        color: "#8492a6",
                      }}
                    >
                      Appraisal moves to HR Normalization after this.
                    </div>

                    <div
                      style={{
                        background: "#f8f9fb",
                        borderRadius: 8,
                        padding: 10,
                        marginTop: 12,
                        fontSize: 13,
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            color: "#8492a6",
                          }}
                        >
                          Adjusted Score
                        </span>

                        <strong>
                          {Number(adjustedScore || 0).toFixed(2)} / 5.00
                        </strong>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            color: "#8492a6",
                          }}
                        >
                          KRA Overrides
                        </span>

                        <strong>{Object.keys(kraOverrides).length}</strong>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span
                          style={{
                            color: "#8492a6",
                          }}
                        >
                          Question Overrides
                        </span>

                        <strong>{Object.keys(questionOverrides).length}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn-og btn-og-secondary"
                    onClick={() => setShowSubmitModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn-og btn-og-primary"
                    type="button"
                    onClick={handleSubmitSkipLevel}
                    disabled={submitting}
                  >
                    <i className="fa fa-check"></i>{" "}
                    {submitting ? "Submitting..." : "Submit to Normalization"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* =====================================================
              SEND BACK MODAL
          ===================================================== */}

          {showSendBackModal && (
            <div className="og-modal-overlay">
              <div className="og-modal">
                <div className="og-modal-header">
                  <div className="og-modal-title">
                    <i
                      className="fa fa-rotate-left"
                      style={{
                        color: "#e53e3e",
                      }}
                    ></i>{" "}
                    Send Back
                  </div>

                  <button
                    className="og-modal-close"
                    onClick={() => setShowSendBackModal(false)}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>

                <div className="og-modal-body">
                  <div className="og-form-group">
                    <label className="og-label">Send Back To *</label>

                    <select className="og-select">
                      <option>Manager (First Reviewer)</option>

                      <option>Employee (Self Assessment)</option>
                    </select>
                  </div>

                  <div className="og-form-group" style={{ marginBottom: 0 }}>
                    <label className="og-label">Reason *</label>

                    <textarea
                      className="og-textarea"
                      placeholder="Clearly explain what needs to be corrected or re-evaluated..."
                    />
                  </div>
                </div>

                <div className="og-modal-footer">
                  <button
                    className="btn-og btn-og-secondary"
                    onClick={() => setShowSendBackModal(false)}
                  >
                    Cancel
                  </button>

                  <button className="btn-og btn-og-danger" type="button">
                    <i className="fa fa-rotate-left"></i> Send Back
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <Suspense>
          <ComponentLoader />
        </Suspense>
      ) : (
        ""
      )}
    </div>
  );
};

export default PMSSkipLevelReview;
